"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useState } from "react";
import { keepPreviousData, useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import useCapacitor from "./useCapacitor";
import { useDebouncedQueryRefetch } from "./useDebouncedQueryRefetch";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { WAVE_DROPS_PARAMS } from "@/components/react-query-wrapper/utils/query-utils";
import { updateAttachmentInCachedDrops, updateDropInCachedDrops } from "@/components/react-query-wrapper/utils/updateAttachmentInCachedDrops";
import type { ApiAttachment } from "@/generated/models/ApiAttachment";
import type { ApiWaveDropsFeed } from "@/generated/models/ApiWaveDropsFeed";
import type { ApiWave } from "@/generated/models/ApiWave";
import { generateUniqueKeys, mapToExtendedDrops } from "@/helpers/waves/wave-drops.helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import type { WsDropUpdateMessage } from "@/helpers/Types";
import { WsMessageType } from "@/helpers/Types";
import { useWebSocketMessage } from "@/services/websocket/useWebSocketMessage";
import { fetchDropRepliesV2, type ApiWaveDropsV2PageFeed } from "@/services/api/wave-drops-v2-api";
export function useDropMessages(waveId, dropId, wave) {
  const $ = _c(42);
  const {
    isCapacitor
  } = useCapacitor();
  const queryClient = useQueryClient();
  const [init, setInit] = useState(false);
  let t0;
  if ($[0] !== dropId || $[1] !== waveId) {
    t0 = [QueryKey.DROPS, {
      waveId,
      limit: WAVE_DROPS_PARAMS.limit,
      dropId
    }];
    $[0] = dropId;
    $[1] = waveId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const queryKey = t0;
  let t1;
  if ($[3] !== dropId || $[4] !== wave) {
    t1 = async t2 => {
      const {
        pageParam
      } = t2;
      return fetchDropRepliesV2({
        parentDropId: dropId ?? "",
        page: pageParam?.page ?? 1,
        pageSize: WAVE_DROPS_PARAMS.limit,
        wave
      });
    };
    $[3] = dropId;
    $[4] = wave;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const t2 = !!dropId;
  const t3 = !isCapacitor;
  let t4;
  if ($[6] !== queryKey || $[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t4 = {
      queryKey,
      queryFn: t1,
      enabled: t2,
      initialPageParam: null,
      getNextPageParam: _temp,
      placeholderData: keepPreviousData,
      staleTime: 60000,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true,
      refetchIntervalInBackground: t3
    };
    $[6] = queryKey;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const {
    data,
    fetchNextPage: onFetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    refetch
  } = useInfiniteQuery(t4);
  let t5;
  if ($[11] !== hasNextPage || $[12] !== isFetchingNextPage || $[13] !== onFetchNextPage) {
    t5 = async () => {
      if (hasNextPage && !isFetchingNextPage) {
        await onFetchNextPage();
      }
    };
    $[11] = hasNextPage;
    $[12] = isFetchingNextPage;
    $[13] = onFetchNextPage;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const fetchNextPage = t5;
  const processDrops = _temp3;
  let t6;
  if ($[15] !== data?.pages) {
    t6 = () => processDrops(data?.pages, [], false);
    $[15] = data?.pages;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const [drops, setDrops] = useState(t6);
  let t7;
  let t8;
  if ($[17] !== data) {
    t7 = () => {
      if (!data) {
        return;
      }
      setDrops(prev => processDrops(data?.pages, prev, false));
      setInit(true);
    };
    t8 = [data];
    $[17] = data;
    $[18] = t7;
    $[19] = t8;
  } else {
    t7 = $[18];
    t8 = $[19];
  }
  useEffect(t7, t8);
  let t9;
  if ($[20] !== isFetching || $[21] !== isFetchingNextPage || $[22] !== refetch) {
    t9 = {
      refetch,
      isFetching,
      isFetchingNextPage
    };
    $[20] = isFetching;
    $[21] = isFetchingNextPage;
    $[22] = refetch;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const requestRefetch = useDebouncedQueryRefetch(t9);
  let t10;
  if ($[24] !== dropId || $[25] !== queryClient || $[26] !== requestRefetch || $[27] !== waveId) {
    t10 = message => {
      if (!dropId) {
        return;
      }
      if (waveId !== message.wave.id) {
        return;
      }
      updateDropInCachedDrops(queryClient, message, {
        preferExistingPollVote: true
      });
      requestRefetch();
    };
    $[24] = dropId;
    $[25] = queryClient;
    $[26] = requestRefetch;
    $[27] = waveId;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  useWebSocketMessage(WsMessageType.DROP_UPDATE, t10);
  let t11;
  if ($[29] !== queryClient) {
    t11 = attachment => {
      updateAttachmentInCachedDrops(queryClient, attachment);
    };
    $[29] = queryClient;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  useWebSocketMessage(WsMessageType.ATTACHMENT_STATUS_UPDATE, t11);
  let t12;
  if ($[31] !== fetchNextPage || $[32] !== hasNextPage) {
    t12 = async () => {
      if (hasNextPage) {
        await fetchNextPage();
      }
    };
    $[31] = fetchNextPage;
    $[32] = hasNextPage;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  const manualFetch = t12;
  const t13 = isFetching ?? !init;
  let t14;
  if ($[34] !== drops || $[35] !== fetchNextPage || $[36] !== hasNextPage || $[37] !== isFetchingNextPage || $[38] !== manualFetch || $[39] !== refetch || $[40] !== t13) {
    t14 = {
      drops,
      fetchNextPage,
      hasNextPage,
      isFetching: t13,
      isFetchingNextPage,
      refetch,
      manualFetch
    };
    $[34] = drops;
    $[35] = fetchNextPage;
    $[36] = hasNextPage;
    $[37] = isFetchingNextPage;
    $[38] = manualFetch;
    $[39] = refetch;
    $[40] = t13;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  return t14;
}
function _temp3(pages, previousDrops, isReverse) {
  const newDrops = pages ? mapToExtendedDrops(pages.map(_temp2), previousDrops, isReverse) : [];
  return generateUniqueKeys(newDrops, previousDrops);
}
function _temp2(page) {
  return {
    wave: page.wave,
    drops: page.drops
  };
}
function _temp(lastPage) {
  return lastPage.next ? {
    page: lastPage.page + 1
  } : null;
}